import React from "react";

/**
 * App-root error boundary.
 *
 * Catches any uncaught error during the initial render so Safari / iOS
 * users (who are most likely to hit odd runtime quirks like Private
 * Browsing storage throws) see a readable fallback instead of a blank
 * white screen.
 *
 * Logs to console + exposes a plain "Reload" affordance.
 */
export class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    try {
      console.error("App crashed on first render:", error, info);
    } catch (_) {}
  }

  render() {
    if (!this.state.error) return this.props.children;

    const msg =
      (this.state.error && (this.state.error.message || String(this.state.error))) ||
      "An unknown error occurred.";

    return (
      <div
        data-testid="root-error-boundary"
        style={{
          minHeight: "100vh",
          background: "#07080A",
          color: "#E2E8F0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          textAlign: "center",
        }}
      >
        <img
          src="/apple-touch-icon.png"
          alt="ProseCraft"
          style={{
            width: 72,
            height: 72,
            borderRadius: 16,
            border: "1px solid rgba(255,255,255,0.1)",
            marginBottom: 20,
          }}
        />
        <h1 style={{ fontSize: 22, margin: 0, color: "#F8FAFC", fontWeight: 600 }}>
          Something misfired loading ProseCraft
        </h1>
        <p style={{ marginTop: 8, maxWidth: 420, color: "#94A3B8", fontSize: 14, lineHeight: 1.5 }}>
          If you're on Safari in Private Browsing, try opening the site in a regular tab —
          Private mode blocks the storage we need to keep you signed in.
        </p>
        <pre
          style={{
            marginTop: 16,
            padding: "10px 14px",
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 8,
            color: "#FCA5A5",
            fontSize: 11,
            maxWidth: 480,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {msg}
        </pre>
        <button
          data-testid="root-error-reload"
          onClick={() => {
            try { window.location.reload(); } catch (_) {}
          }}
          style={{
            marginTop: 20,
            padding: "10px 20px",
            background: "#F59E0B",
            color: "#000",
            borderRadius: 999,
            border: "none",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          Reload page
        </button>
      </div>
    );
  }
}

export default RootErrorBoundary;
